import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { TrendingUp, Users, Clock, Award } from "lucide-react";

export default function Analytics() {
  const { lang } = useLanguage();
  const [peakHours, setPeakHours] = useState({ hours: [], weekdays: [], total_bookings: 0 });
  const [topServices, setTopServices] = useState({ rows: [] });
  const [retention, setRetention] = useState({});
  const [days, setDays] = useState(30);

  useEffect(() => {
    Promise.all([
      api.get(`/analytics/peak-hours?days=${days}`),
      api.get(`/analytics/top-services?days=${days}`),
      api.get(`/analytics/customer-retention?days=${days * 3}`),
    ]).then(([p, t, r]) => { setPeakHours(p.data); setTopServices(t.data); setRetention(r.data); });
  }, [days]);

  const maxHour = Math.max(1, ...peakHours.hours.map((h) => h.count));
  const weekDayNames = lang === "ar" ? ["إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت", "أحد"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "التحليلات المتقدمة" : "Advanced Analytics"}</h1>
        <select className="px-3 py-2 border rounded-md bg-[var(--bg)]" value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
          <option value={7}>{lang === "ar" ? "آخر 7 أيام" : "Last 7 days"}</option>
          <option value={30}>{lang === "ar" ? "آخر 30 يومًا" : "Last 30 days"}</option>
          <option value={90}>{lang === "ar" ? "آخر 90 يومًا" : "Last 90 days"}</option>
        </select>
      </div>

      {/* Stats cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
          <Clock className="text-[var(--gold)] mb-2" />
          <div className="text-2xl font-bold">{peakHours.total_bookings}</div>
          <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "إجمالي الحجوزات" : "Total Bookings"}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
          <Users className="text-[var(--rose)] mb-2" />
          <div className="text-2xl font-bold">{retention.total_customers || 0}</div>
          <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "إجمالي العملاء" : "Total Customers"}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
          <TrendingUp className="text-green-600 mb-2" />
          <div className="text-2xl font-bold">{retention.retention_rate || 0}%</div>
          <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "نسبة الاحتفاظ" : "Retention Rate"}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
          <Award className="text-amber-600 mb-2" />
          <div className="text-2xl font-bold">{retention.avg_visits || 0}</div>
          <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "متوسط الزيارات" : "Avg Visits/Customer"}</div>
        </div>
      </div>

      {/* Peak Hours */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
        <h2 className="font-heading text-lg mb-4">{lang === "ar" ? "ساعات الذروة" : "Peak Hours"}</h2>
        <div className="grid grid-cols-12 gap-1 items-end h-40">
          {peakHours.hours.map((h) => (
            <div key={h.hour} className="flex flex-col items-center gap-1">
              <div
                className="w-full rounded-t transition"
                style={{
                  height: `${(h.count / maxHour) * 100}%`,
                  background: `linear-gradient(to top, #7A1F3D, #9B2950)`,
                  minHeight: h.count > 0 ? "8px" : "0",
                }}
                title={`${h.hour}:00 - ${h.count}`}
              />
              <span className="text-xs text-[var(--text-muted)]">{h.hour}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Weekdays */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
        <h2 className="font-heading text-lg mb-4">{lang === "ar" ? "الحجوزات حسب اليوم" : "Bookings by Day"}</h2>
        <div className="grid grid-cols-7 gap-2">
          {peakHours.weekdays.map((d, i) => {
            const max = Math.max(1, ...peakHours.weekdays.map((x) => x.count));
            return (
              <div key={d.day} className="text-center">
                <div className="bg-[var(--bg)] rounded-md h-24 flex items-end overflow-hidden">
                  <div className="w-full bg-gradient-to-t from-[var(--gold)] to-[var(--rose)] transition" style={{ height: `${(d.count / max) * 100}%` }} />
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-2">{weekDayNames[i]}</div>
                <div className="text-sm font-semibold">{d.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Services */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
        <h2 className="font-heading text-lg mb-4">{lang === "ar" ? "أعلى الخدمات ربحًا" : "Most Profitable Services"}</h2>
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)]">
            <tr><th className="p-2 text-start">{lang === "ar" ? "الخدمة" : "Service"}</th><th className="p-2 text-end">{lang === "ar" ? "المرات" : "Count"}</th><th className="p-2 text-end">{lang === "ar" ? "الإيرادات" : "Revenue"}</th></tr>
          </thead>
          <tbody>
            {topServices.rows.map((r) => (
              <tr key={r.service_id} className="border-t border-[var(--border)]">
                <td className="p-2">{lang === "ar" ? r.name_ar : r.name_en}</td>
                <td className="p-2 text-end">{r.count}</td>
                <td className="p-2 text-end font-semibold text-[var(--gold)]">{r.revenue}</td>
              </tr>
            ))}
            {topServices.rows.length === 0 && <tr><td colSpan={3} className="text-center text-[var(--text-muted)] py-6">{lang === "ar" ? "لا توجد بيانات" : "No data"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
